import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const category = categories.find((c) => c.slug === slug);

  // If category not found, redirect to home
  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类不存在</h1>
          <p className="text-foreground-500 mb-4">您访问的分类页面不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles
    .filter((a) => a.categoryId === category.id)
    .sort((a, b) => {
      if (sortBy === 'hot') return b.views - a.views;
      return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黑料吃瓜网`,
    description: category.description,
    url: `http://9288c.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料吃瓜网',
      url: 'http://9288c.com/',
    },
    about: {
      '@type': 'Thing',
      name: category.name,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://9288c.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `http://9288c.com/category/${category.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([categorySchema, breadcrumbSchema]),
        }}
      />

      <Navbar />

      {/* Category Header */}
      <section className="relative pt-20 md:pt-24 pb-8 md:pb-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className={`${category.icon} text-background-50 text-2xl md:text-3xl`} />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1 max-w-xl">
                {category.description}
              </p>
            </div>
            <div className="md:ml-auto flex items-center gap-3">
              <span className="text-sm text-foreground-400">
                共 {categoryArticles.length} 篇
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-[56px] md:top-[60px] z-30 bg-background-50/95 backdrop-blur-sm border-y border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setSortBy('latest')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  sortBy === 'latest'
                    ? 'bg-background-50 text-primary-600 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-time-line mr-1" />
                最新
              </button>
              <button
                onClick={() => setSortBy('hot')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  sortBy === 'hot'
                    ? 'bg-background-50 text-primary-600 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-fire-line mr-1" />
                最热
              </button>
            </div>

            <div className="flex items-center gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`hidden md:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <main className="py-8 md:py-12 px-4 md:px-6" role="main">
        <div className="max-w-7xl mx-auto">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-foreground-400 text-2xl" />
              </div>
              <h3 className="text-lg font-medium text-foreground-700 mb-2">
                暂无内容
              </h3>
              <p className="text-sm text-foreground-400">
                该分类下暂时没有相关文章，去看看其他分类吧
              </p>
            </div>
          )}

          {/* Pagination (mock) */}
          {categoryArticles.length > 6 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-400 hover:bg-background-100 transition-colors disabled:opacity-50" disabled>
                <i className="ri-arrow-left-s-line" />
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium">
                1
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 text-sm transition-colors">
                2
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 text-sm transition-colors">
                3
              </button>
              <span className="text-foreground-400 px-1">...</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 text-sm transition-colors">
                10
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-400 hover:bg-background-100 transition-colors">
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Related Categories */}
      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
            其他分类
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 p-3 md:p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200/50 transition-all"
                >
                  <div className="w-10 h-10 bg-background-100 group-hover:bg-primary-50 rounded-lg flex items-center justify-center transition-colors flex-shrink-0">
                    <i className={`${cat.icon} text-foreground-500 group-hover:text-primary-500 transition-colors`} />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {cat.articleCount} 篇
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}